import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiChevronRight } from 'react-icons/fi';
import { useTelegram } from '../../hooks/useTelegram';
import { gameAPI } from '../../services/api';

const PROFILE_KEY = 'openps_profile';
const getProfileConsole = () => {
  try { const p = JSON.parse(localStorage.getItem(PROFILE_KEY)); return p?.console || 'ps5'; }
  catch { return 'ps5'; }
};
// GameCard used in collections inline

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #000000;
  padding-bottom: 16px;
`;

const SearchBar = styled.div`
  margin: 12px 16px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.04);
  border-radius: 14px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: rgba(255,255,255,0.3);
  font-size: 15px;
  transition: all 0.2s;
  &:active { background: rgba(255,255,255,0.1); }
`;

/* Promo — reloc-style card carousel with padding */
const PromoWrap = styled.div`
  margin-bottom: 24px;
  animation: ${fadeUp} 0.5s ease both;
`;

const PromoTrack = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  -webkit-overflow-scrolling: touch;
  padding: 0 16px;
  gap: 12px;
`;

const PromoSlide = styled.div`
  flex-shrink: 0;
  width: calc(100vw - 64px);
  max-width: 340px;
  aspect-ratio: 3/2;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  scroll-snap-align: center;
  position: relative;
  background: #14141f;
  &:active { transform: scale(0.97); }
  transition: transform 0.15s;
`;

const PromoBannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PromoOverlay = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 10 L45 25 L45 40 L30 50 L15 40 L15 25 Z' fill='none' stroke='white' stroke-width='1'/%3E%3C/svg%3E");
  background-size: 60px 60px;
  pointer-events: none;
`;

const PromoBigText = styled.div`
  font-size: ${p => p.$size || '28px'};
  font-weight: 900;
  color: #fff;
  line-height: 1.1;
  text-transform: uppercase;
  position: relative;
  z-index: 1;
  text-shadow: 0 2px 20px rgba(0,0,0,0.3);
`;

const PromoAccent = styled.div`
  font-size: ${p => p.$size || '64px'};
  font-weight: 900;
  color: ${p => p.$color || '#fff'};
  line-height: 1;
  position: relative;
  z-index: 1;
  text-shadow: 0 4px 30px rgba(0,0,0,0.3);
  margin: 8px 0;
`;

const PromoSubText = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
  position: relative;
  z-index: 1;
  margin-top: 4px;
`;

const PromoPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${p => p.$bg || 'rgba(255,255,255,0.2)'};
  backdrop-filter: blur(8px);
  padding: 8px 18px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  position: relative;
  z-index: 1;
  margin-top: 12px;
`;

const PromoImg = styled.img`
  width: ${p => p.$w || '120px'};
  height: auto;
  position: relative;
  z-index: 1;
  filter: drop-shadow(0 8px 24px rgba(0,0,0,0.4));
  margin: 8px 0;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;
`;

const Dot = styled.div`
  width: ${p => p.$on ? '20px' : '6px'};
  height: 6px;
  border-radius: 3px;
  background: ${p => p.$on ? '#006FFF' : 'rgba(255,255,255,0.15)'};
  transition: all 0.3s;
`;

/* Sections */
const Section = styled.section`
  margin-bottom: 0;
  padding: 12px 0;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: ${p => p.$d || '0s'};
  background: ${p => p.$dark ? 'rgba(255,255,255,0.04)' : 'transparent'};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  margin-bottom: 8px;
`;

const SectionTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  letter-spacing: -0.3px;
`;

const SeeAll = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  color: #006FFF;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:active { opacity: 0.7; }
`;

/* Categories */
const CatRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 16px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const CatChip = styled.div`
  flex-shrink: 0;
  background: ${p => p.$bg || '#14141f'};
  border: 1px solid rgba(255,255,255,0.04);
  border-radius: 14px;
  padding: 16px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 80px;
  overflow: hidden;
  transition: transform 0.15s;
  &:active { transform: scale(0.97); }
`;

const CatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${p => p.$iconBg || 'rgba(0,111,255,0.15)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const CatIconImg = styled.img`
  width: 150px;
  height: 150px;
  object-fit: contain;
  margin: -25px;
`;

const CatInfo = styled.div``;

const CatName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
`;

const CatCount = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.35);
  margin-top: 2px;
`;

/* PS Plus — reloc-style vertical list with pill switcher */
const PlusSectionWrap = styled.div`
  padding: 0 16px;
`;

const PlusPillRow = styled.div`
  display: flex;
  background: rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 2px;
  margin-bottom: 8px;
`;

const PlusPill = styled.div`
  flex: 1;
  text-align: center;
  padding: 7px 0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: ${p => p.$active ? '#fff' : 'rgba(255,255,255,0.45)'};
  background: ${p => p.$active ? '#006FFF' : 'transparent'};
`;

const PlusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  cursor: pointer;
  transition: transform 0.15s;
  &:active { transform: scale(0.98); }
`;

const PlusRowImg = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
  background: #1a1a2e;
`;

const PlusRowInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlusRowPrice = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
`;

const PlusRowName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.45);
  margin-top: 2px;
`;

const EaPlaySubtitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 12px 0 4px;
`;

/* Data */
/* Banner configs — image-based banners */
const PROMOS = [
  {
    id: 0,
    type: 'giveaway',
    image: '/banners/giveaway-banner.jpg',
    route: 'https://t.me/open_ps',
  },
  {
    id: 1,
    type: 'sales',
    image: '/banners/sale.jpg',
    route: '/catalog?onSale=true',
  },
  {
    id: 2,
    type: 'psplus',
    image: '/banners/psplus.jpg',
    route: '/psplus',
  },
  {
    id: 3,
    type: 'topup',
    image: '/banners/topup.png',
    route: '/donates',
  },
  {
    id: 4,
    type: 'eaplay',
    image: '/banners/eaplay.jpg',
    route: '/psplus',
  },
  {
    id: 5,
    type: 'coop',
    image: '/banners/coop.jpg',
    route: '/catalog',
  },
];

/* removed old tier constants */

const COLLECTION_ROUTES = {
  popular: '/catalog?sort=rating',
  sales: '/catalog?onSale=true',
  new: '/catalog?sort=newest',
  action: '/catalog?genre=Экшен',
  racing: '/catalog?genre=Гонки',
  horror: '/catalog?genre=Хоррор',
  sport: '/catalog?genre=Спортивная',
  rpg: '/catalog?genre=Ролевая',
  shooter: '/catalog?genre=Шутер',
  puzzle: '/catalog?genre=Головоломка',
  vr: '/catalog?type=VR',
  bundles: '/catalog?type=bundle',
  preorders: '/catalog?type=preorder',
  'price-under-1000': '/catalog?maxPrice=1000',
  'price-1000-2000': '/catalog?minPrice=1000&maxPrice=2000',
  'price-2000-3000': '/catalog?minPrice=2000&maxPrice=3000',
  'price-3000-5000': '/catalog?minPrice=3000&maxPrice=5000',
  'price-over-5000': '/catalog?minPrice=5000',
};

/* Collection carousel card — compact 140px */
const CollCard = styled.div`
  flex-shrink: 0;
  width: 140px;
  cursor: pointer;
  scroll-snap-align: start;
  transition: transform 0.25s cubic-bezier(0.33, 1, 0.68, 1), opacity 0.25s ease;
  &:active { transform: scale(0.96); opacity: 0.85; }
`;

const CollCover = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 12px;
  overflow: hidden;
  background: #1a1a2e;
  position: relative;
`;

const CollImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CollDiscBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  background: ${p => p.$pct >= 50 ? '#ff9500' : '#34c759'};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
`;

const CollTitle = styled.div`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 2.5em;
`;

const CollPrice = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #00d4ff;
  margin-top: 3px;
`;

const CollOldPrice = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: rgba(255,255,255,0.3);
  text-decoration: line-through;
  margin-left: 4px;
`;

const CollDiscLine = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #ff3b30;
  margin-top: 1px;
`;

const CollScroll = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 0 16px;
  scrollbar-width: none;
  scroll-snap-type: x proximity;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  &::-webkit-scrollbar { display: none; }
`;

/* Donate cards — full-cover image with gradient overlay */
const DonateCard = styled.div`
  flex-shrink: 0;
  width: 165px;
  aspect-ratio: 3/4;
  border-radius: 16px;
  overflow: hidden;
  background: #1a1a2e;
  cursor: pointer;
  scroll-snap-align: start;
  position: relative;
  transition: transform 0.25s cubic-bezier(0.33, 1, 0.68, 1), opacity 0.25s ease;
  &:active { transform: scale(0.96); opacity: 0.85; }
`;

const ServiceBg = styled.div`
  position: absolute;
  inset: 0;
  background: ${p => p.$bg || '#1a1a2e'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
`;

const ServiceIcon = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.3));
`;

const DonateImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const DonateOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 40%, transparent 60%);
  pointer-events: none;
`;

const DonateInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 14px;
`;

const DonateName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  line-height: 1.25;
  margin-bottom: 4px;
`;

const DonatePrice = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #00d4ff;
`;

const Home = () => {
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const [featured, setFeatured] = useState(null);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [priceRanges, setPriceRanges] = useState([]);
  const [donateGroups, setDonateGroups] = useState([]);
  const [psPlusAll, setPsPlusAll] = useState([]);
  const [plusDur, setPlusDur] = useState('1m');
  const [promoIdx, setPromoIdx] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    if (tg) { tg.setHeaderColor('#0a0a0f'); tg.BackButton.hide(); }
    gameAPI.getFeatured().then(setFeatured).catch(() => {});
    gameAPI.getCategories().then(setCategories).catch(() => {});
    gameAPI.getCollections().then(cols => {
      const userConsole = getProfileConsole();
      let filtered = (cols || []).filter(c => c.id !== 'psplus');
      // Если у пользователя PS4 — фильтруем игры (только PS4)
      if (userConsole === 'ps4') {
        filtered = filtered.map(col => ({
          ...col,
          games: (col.games || []).filter(g => 
            g.platforms?.includes('PS4') || g.platform === 'PS4' || !g.platforms?.includes('PS5')
          )
        })).filter(col => col.games.length > 0);
      }
      setCollections(filtered);
    }).catch(() => {});
    gameAPI.getPriceRanges().then(setPriceRanges).catch(() => {});
    gameAPI.getPSPlus().then(items => setPsPlusAll((items || []).filter(x => x.price_rub > 0))).catch(() => {});
    gameAPI.getDonates().then(items => {
      const SUBTYPE_NAMES = {
        wallet: 'PSN Кошелёк',
        fc26: 'EA FC 26',
        fortnite: 'Fortnite V-Bucks',
        cod: 'CoD: BO7 & Warzone',
        apex: 'Apex Legends',
        helldivers: 'Helldivers 2',
        overwatch: 'Overwatch 2',
        pubg: 'PUBG',
        wot: 'World of Tanks',
        warface: 'Warface',
        diablo: 'Diablo IV',
        rust: 'Rust',
        warthunder: 'War Thunder',
        arc_raiders: 'ARC Raiders',
        // Xbox GamePass moved to /xbox/gamepass
      };
      const groups = {};
      (items || []).forEach(item => {
        const st = item.subtype;
        if (!st || !SUBTYPE_NAMES[st]) return;
        if (!groups[st]) groups[st] = { subtype: st, name: SUBTYPE_NAMES[st], minPrice: item.price_rub, image: item.image, icon: item.icon };
        if (item.price_rub < groups[st].minPrice) {
          groups[st].minPrice = item.price_rub;
        }
        if (!groups[st].image && item.image) groups[st].image = item.image;
        if (!groups[st].icon && item.icon) groups[st].icon = item.icon;
      });
      setDonateGroups(Object.values(groups));
    }).catch(() => {});
  }, [tg]);

  // Auto-swipe promo banners every 4s
  useEffect(() => {
    const timer = setInterval(() => {
      const el = trackRef.current;
      if (!el) return;
      const nextIdx = (promoIdx + 1) % PROMOS.length;
      el.scrollTo({ left: nextIdx * el.offsetWidth, behavior: 'smooth' });
    }, 4000);
    return () => clearInterval(timer);
  }, [promoIdx]);

  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.scrollWidth / PROMOS.length;
    setPromoIdx(Math.round(el.scrollLeft / w));
  }, []);

  const catConfig = {
    'PS5': { img: '/images/categories/ps5-controller.png?v=1', iconBg: 'rgba(0,111,255,0.15)', bg: 'linear-gradient(135deg,#0d1b3e,#14141f)', route: '/catalog?platform=PS5' },
    'PS4': { img: '/images/categories/ps4-controller.png?v=1', iconBg: 'rgba(255,59,48,0.15)', bg: 'linear-gradient(135deg,#3e0d0d,#14141f)', route: '/catalog?platform=PS4' },
    'psplus': { icon: '⭐', iconBg: 'rgba(255,214,10,0.15)', bg: 'linear-gradient(135deg,#3e2e0d,#14141f)', route: '/psplus' },
    'donates': { icon: '💎', iconBg: 'rgba(224,64,251,0.15)', bg: 'linear-gradient(135deg,#2e0d3e,#14141f)', route: '/donates' },
  };

  // Group psplus by tier+duration, pick min price per region
  const plusGrouped = {};
  psPlusAll.forEach(p => {
    const key = `${p.tier}_${p.duration}`;
    if (!plusGrouped[key] || p.price_rub < plusGrouped[key].price_rub) {
      plusGrouped[key] = { ...p };
    }
  });
  // EA Play items grouped by duration
  const eaItems = ['1m', '12m'].map(d => plusGrouped[`ea_play_${d}`]).filter(Boolean);

  return (
    <Page>
      <SearchBar onClick={() => navigate('/search')}>
        <FiSearch size={18} />
        Найти игру, подписку или донат...
      </SearchBar>

      {/* Promo — image banners */}
      <PromoWrap>
        <PromoTrack ref={trackRef} onScroll={onScroll}>
          {PROMOS.map(p => (
            <PromoSlide key={p.id} onClick={() => {
              const r = p.route || '/catalog';
              if (r.startsWith('http')) { window.open(r, '_blank'); } else { navigate(r); }
            }}>
              <PromoBannerImg src={p.image} alt="" loading="lazy" />
            </PromoSlide>
          ))}
        </PromoTrack>
        <Dots>
          {PROMOS.map((_, i) => <Dot key={i} $on={i === promoIdx} />)}
        </Dots>
      </PromoWrap>

      {/* PS Plus — reloc style */}
      <Section $d="0.05s">
        <SectionHeader>
          <SectionTitle>Подписки PS Plus</SectionTitle>
          <SeeAll onClick={() => navigate('/psplus')}>Все <FiChevronRight size={14} /></SeeAll>
        </SectionHeader>
        <PlusSectionWrap>
          <PlusPillRow>
            {[['1m','1 месяц'],['3m','3 месяца'],['12m','12 месяцев']].map(([d,label]) => (
              <PlusPill key={d} $active={plusDur === d} onClick={() => setPlusDur(d)}>{label}</PlusPill>
            ))}
          </PlusPillRow>
          {['essential','extra','deluxe'].map(tier => {
            const item = plusGrouped[`${tier}_${plusDur}`];
            if (!item) return null;
            return (
              <PlusRow key={tier} onClick={() => navigate('/psplus')}>
                <PlusRowImg src={item.image} alt={tier} loading="lazy" />
                <PlusRowInfo>
                  <PlusRowPrice>от {item.price_rub.toLocaleString('ru')} ₽</PlusRowPrice>
                  <PlusRowName>PS Plus {tier.charAt(0).toUpperCase() + tier.slice(1)}</PlusRowName>
                </PlusRowInfo>
              </PlusRow>
            );
          })}
          {eaItems.length > 0 && (
            <>
              <EaPlaySubtitle>Подписки EA Play</EaPlaySubtitle>
              {eaItems.map(item => (
                <PlusRow key={item.id} onClick={() => navigate('/psplus')}>
                  <PlusRowImg src={item.image} alt="EA Play" loading="lazy" />
                  <PlusRowInfo>
                    <PlusRowPrice>от {item.price_rub.toLocaleString('ru')} ₽</PlusRowPrice>
                    <PlusRowName>EA Play — {item.duration === '1m' ? '1 месяц' : '12 месяцев'}</PlusRowName>
                  </PlusRowInfo>
                </PlusRow>
              ))}
            </>
          )}
        </PlusSectionWrap>
      </Section>

      {/* Скидки — наверху */}
      {collections.filter(c => c.id === 'sales').map((col) => (
        <Section key={col.id} $d="0.12s" $dark>
          <SectionHeader>
            <SectionTitle>{col.title}</SectionTitle>
            <SeeAll onClick={() => navigate(COLLECTION_ROUTES[col.id] || '/catalog')}>Все <FiChevronRight size={14} /></SeeAll>
          </SectionHeader>
          <CollScroll>
            {col.games.map(g => (
              <DonateCard key={g.id} onClick={() => navigate(`/game/${encodeURIComponent(g.id || g.sony_id)}`)}>
                {g.image_url && <DonateImg src={g.image_url} alt={g.title} loading="lazy" referrerPolicy="no-referrer" />}
                <DonateOverlay />
                {g._discount > 0 && <CollDiscBadge $pct={g._discount}>−{g._discount}%</CollDiscBadge>}
                <DonateInfo>
                  <DonateName>{g.title}</DonateName>
                  <DonatePrice>
                    от {g._minPrice ? g._minPrice.toLocaleString('ru') : 0} ₽
                    {g._minOld && <CollOldPrice style={{color:'rgba(255,255,255,.5)'}}>{g._minOld.toLocaleString('ru')} ₽</CollOldPrice>}
                  </DonatePrice>
                </DonateInfo>
              </DonateCard>
            ))}
          </CollScroll>
        </Section>
      ))}

      {/* Console picker — PS5 / PS4 */}
      <Section $d="0.13s">
        <SectionHeader>
          <SectionTitle>Ваша приставка</SectionTitle>
        </SectionHeader>
        <CatRow>
          {[
            { id: 'PS5', name: 'PS5', count: categories.find(c => c.id === 'PS5')?.count || 0 },
            { id: 'PS4', name: 'PS4', count: categories.find(c => c.id === 'PS4')?.count || 0 },
          ].map(c => {
            const conf = catConfig[c.id] || {};
            return (
              <CatChip key={c.id} $bg={conf.bg} onClick={() => navigate(conf.route || '/catalog')} style={{flex:'1'}}>
                {conf.img ? <CatIconImg src={conf.img} alt={c.name} /> : <CatIcon $iconBg={conf.iconBg}>{conf.icon || '🎯'}</CatIcon>}
                <CatInfo>
                  <CatName>{c.name}</CatName>
                  <CatCount>{c.count} шт.</CatCount>
                </CatInfo>
              </CatChip>
            );
          })}
        </CatRow>
      </Section>

      {/* Новинки */}
      {collections.filter(c => c.id === 'new').map((col) => (
        <Section key={col.id} $d="0.14s" $dark>
          <SectionHeader>
            <SectionTitle>{col.title}</SectionTitle>
            <SeeAll onClick={() => navigate(COLLECTION_ROUTES[col.id] || '/catalog?sort=newest')}>Все <FiChevronRight size={14} /></SeeAll>
          </SectionHeader>
          <CollScroll>
            {col.games.map(g => (
              <DonateCard key={g.id} onClick={() => navigate(`/game/${encodeURIComponent(g.id || g.sony_id)}`)}>
                {g.image_url && <DonateImg src={g.image_url} alt={g.title} loading="lazy" referrerPolicy="no-referrer" />}
                <DonateOverlay />
                {g._discount > 0 && <CollDiscBadge $pct={g._discount}>−{g._discount}%</CollDiscBadge>}
                <DonateInfo>
                  <DonateName>{g.title}</DonateName>
                  <DonatePrice>
                    от {g._minPrice ? g._minPrice.toLocaleString('ru') : 0} ₽
                  </DonatePrice>
                </DonateInfo>
              </DonateCard>
            ))}
          </CollScroll>
        </Section>
      ))}

      {/* Хиты продаж — большие карточки */}
      {collections.filter(c => c.id === 'popular').map((col) => (
        <Section key={col.id} $d="0.15s">
          <SectionHeader>
            <SectionTitle>{col.title}</SectionTitle>
            <SeeAll onClick={() => navigate(COLLECTION_ROUTES[col.id] || '/catalog')}>Все <FiChevronRight size={14} /></SeeAll>
          </SectionHeader>
          <CollScroll>
            {col.games.map(g => (
              <DonateCard key={g.id} onClick={() => navigate(`/game/${encodeURIComponent(g.id || g.sony_id)}`)}>
                {g.image_url && <DonateImg src={g.image_url} alt={g.title} loading="lazy" referrerPolicy="no-referrer" />}
                <DonateOverlay />
                {g._discount > 0 && <CollDiscBadge $pct={g._discount}>−{g._discount}%</CollDiscBadge>}
                <DonateInfo>
                  <DonateName>{g.title}</DonateName>
                  <DonatePrice>
                    от {g._minPrice ? g._minPrice.toLocaleString('ru') : 0} ₽
                    {g._minOld && <CollOldPrice style={{color:'rgba(255,255,255,.5)'}}>{g._minOld.toLocaleString('ru')} ₽</CollOldPrice>}
                  </DonatePrice>
                </DonateInfo>
              </DonateCard>
            ))}
          </CollScroll>
        </Section>
      ))}

      {/* Скидки moved above Новинки */}

      {/* Donates (5th carousel) */}
      {donateGroups.length > 0 && (
        <Section $d="0.27s" $dark>
          <SectionHeader>
            <SectionTitle>🪙 Игровой донат</SectionTitle>
            <SeeAll onClick={() => navigate('/donates')}>Все <FiChevronRight size={14} /></SeeAll>
          </SectionHeader>
          <CollScroll>
            {donateGroups.map(g => (
              <DonateCard key={g.subtype} onClick={() => navigate(`/donates/${g.subtype}`)}>
                {g.image && <DonateImg src={`https://open-ps.ru${g.image}`} alt={g.name} loading="lazy" />}
                <DonateOverlay />
                <DonateInfo>
                  <DonateName>{g.name}</DonateName>
                  <DonatePrice>от {g.minPrice?.toLocaleString('ru')} ₽</DonatePrice>
                </DonateInfo>
              </DonateCard>
            ))}
          </CollScroll>
        </Section>
      )}

      {/* До 1000₽ */}
      {priceRanges.filter(r => r.max === 1000).map((range) => (
        <Section key="under1000" $d="0.3s">
          <SectionHeader>
            <SectionTitle>💰 {range.label}</SectionTitle>
            <SeeAll onClick={() => navigate(`/catalog?minPrice=${range.min}&maxPrice=${range.max}`)}>
              {range.total} шт. <FiChevronRight size={14} />
            </SeeAll>
          </SectionHeader>
          <CollScroll>
            {range.games.map(g => (
              <DonateCard key={g.id} onClick={() => navigate(`/game/${encodeURIComponent(g.id || g.sony_id)}`)}>
                {g.image_url && <DonateImg src={g.image_url} alt={g.title} loading="lazy" referrerPolicy="no-referrer" />}
                <DonateOverlay />
                <DonateInfo>
                  <DonateName>{g.title}</DonateName>
                  <DonatePrice>от {g.min_price ? g.min_price.toLocaleString('ru') : 0} ₽</DonatePrice>
                </DonateInfo>
              </DonateCard>
            ))}
          </CollScroll>
        </Section>
      ))}

      {/* VR */}
      {collections.filter(c => c.id === 'vr').map((col) => (
        <Section key={col.id} $d="0.33s" $dark>
          <SectionHeader>
            <SectionTitle>🥽 VR игры</SectionTitle>
            <SeeAll onClick={() => navigate(COLLECTION_ROUTES[col.id] || '/catalog?type=VR')}>Все <FiChevronRight size={14} /></SeeAll>
          </SectionHeader>
          <CollScroll>
            {col.games.map(g => (
              <DonateCard key={g.id} onClick={() => navigate(`/game/${encodeURIComponent(g.id || g.sony_id)}`)}>
                {g.image_url && <DonateImg src={g.image_url} alt={g.title} loading="lazy" referrerPolicy="no-referrer" />}
                <DonateOverlay />
                {g._discount > 0 && <CollDiscBadge $pct={g._discount}>−{g._discount}%</CollDiscBadge>}
                <DonateInfo>
                  <DonateName>{g.title}</DonateName>
                  <DonatePrice>
                    от {g._minPrice ? g._minPrice.toLocaleString('ru') : 0} ₽
                    {g._minOld && <CollOldPrice style={{color:'rgba(255,255,255,.5)'}}>{g._minOld.toLocaleString('ru')} ₽</CollOldPrice>}
                  </DonatePrice>
                </DonateInfo>
              </DonateCard>
            ))}
          </CollScroll>
        </Section>
      ))}

      {/* Наборы игр */}
      {collections.filter(c => c.id === 'bundles').map((col) => (
        <Section key={col.id} $d="0.35s" $dark>
          <SectionHeader>
            <SectionTitle>{col.title}</SectionTitle>
            <SeeAll onClick={() => navigate(COLLECTION_ROUTES[col.id] || '/catalog?type=bundle')}>Все <FiChevronRight size={14} /></SeeAll>
          </SectionHeader>
          <CollScroll>
            {col.games.map(g => (
              <DonateCard key={g.id} onClick={() => navigate(`/game/${encodeURIComponent(g.id || g.sony_id)}`)}>
                {g.image_url && <DonateImg src={g.image_url} alt={g.title} loading="lazy" referrerPolicy="no-referrer" />}
                <DonateOverlay />
                {g._discount > 0 && <CollDiscBadge $pct={g._discount}>−{g._discount}%</CollDiscBadge>}
                <DonateInfo>
                  <DonateName>{g.title}</DonateName>
                  <DonatePrice>от {g._minPrice ? g._minPrice.toLocaleString('ru') : 0} ₽</DonatePrice>
                </DonateInfo>
              </DonateCard>
            ))}
          </CollScroll>
        </Section>
      ))}

      {/* Предзаказы — disabled */}
      {false && collections.filter(c => c.id === 'preorders').map((col) => (
        <Section key={col.id} $d="0.36s">
          <SectionHeader>
            <SectionTitle>{col.title}</SectionTitle>
            <SeeAll onClick={() => navigate(COLLECTION_ROUTES[col.id] || '/catalog?type=preorder')}>Все <FiChevronRight size={14} /></SeeAll>
          </SectionHeader>
          <CollScroll>
            {col.games.map(g => (
              <DonateCard key={g.id} onClick={() => navigate(`/game/${encodeURIComponent(g.id || g.sony_id)}`)}>
                {g.image_url && <DonateImg src={g.image_url} alt={g.title} loading="lazy" referrerPolicy="no-referrer" />}
                <DonateOverlay />
                {g._discount > 0 && <CollDiscBadge $pct={g._discount}>−{g._discount}%</CollDiscBadge>}
                <DonateInfo>
                  <DonateName>{g.title}</DonateName>
                  <DonatePrice>от {g._minPrice ? g._minPrice.toLocaleString('ru') : 0} ₽</DonatePrice>
                </DonateInfo>
              </DonateCard>
            ))}
          </CollScroll>
        </Section>
      ))}

      {/* Зарубежные сервисы */}
      <Section $d="0.38s">
        <SectionHeader>
          <SectionTitle>🌍 Зарубежные сервисы</SectionTitle>
        </SectionHeader>
        <CollScroll>
          {[
            { id: 'spotify', icon: '/icons/spotify.svg', name: 'Spotify', bg: 'linear-gradient(135deg, #1DB954 0%, #1aa34a 50%, #158a3c 100%)' },
            { id: 'chatgpt', icon: '/icons/chatgpt.svg', name: 'ChatGPT', bg: 'linear-gradient(135deg, #10a37f 0%, #0d8a6a 50%, #0a7055 100%)' },
            { id: 'netflix', icon: '/icons/netflix.svg', name: 'Netflix', bg: 'linear-gradient(135deg, #E50914 0%, #b20710 50%, #831010 100%)' },
            { id: 'youtube', icon: '/icons/youtube.svg', name: 'YouTube Premium', bg: 'linear-gradient(135deg, #FF0000 0%, #cc0000 50%, #990000 100%)' },
            { id: 'discord', icon: '/icons/discord.svg', name: 'Discord Nitro', bg: 'linear-gradient(135deg, #5865F2 0%, #4752c4 50%, #3c46a8 100%)' },
          ].map(svc => (
            <DonateCard key={svc.id} onClick={() => navigate(`/services?type=${svc.id}`)}>
              <ServiceBg $bg={svc.bg}><ServiceIcon src={svc.icon} alt={svc.name} /></ServiceBg>
              <DonateOverlay />
              <DonateInfo>
                <DonateName>{svc.name}</DonateName>
              </DonateInfo>
            </DonateCard>
          ))}
        </CollScroll>
      </Section>

      {/* Xbox */}
      <Section $d="0.39s" $dark>
        <SectionHeader>
          <SectionTitle>🎮 Xbox</SectionTitle>
        </SectionHeader>
        <CollScroll>
          {[
            { id: 'gamepass', icon: '/icons/gamepass.svg', name: 'Game Pass Ultimate', bg: 'linear-gradient(135deg, #107C10 0%, #0e6b0e 50%, #0a5a0a 100%)' },
            { id: 'xbox-games', icon: '/icons/xbox.svg', name: 'Игры Xbox', bg: 'linear-gradient(135deg, #0e7a0d 0%, #0b6b0b 50%, #085a08 100%)' },
          ].map(item => (
            <DonateCard key={item.id} onClick={() => navigate(`/xbox?type=${item.id}`)}>
              <ServiceBg $bg={item.bg}><ServiceIcon src={item.icon} alt={item.name} /></ServiceBg>
              <DonateOverlay />
              <DonateInfo>
                <DonateName>{item.name}</DonateName>
              </DonateInfo>
            </DonateCard>
          ))}
        </CollScroll>
      </Section>

      {/* Жанры */}
      <Section $d="0.42s">
        <SectionHeader>
          <SectionTitle>🎭 Жанры</SectionTitle>
        </SectionHeader>
        <CollScroll>
          {[
            { id: 'action', name: 'Экшен', img: '/icons/genre-action.jpg' },
            { id: 'racing', name: 'Гонки', img: '/icons/genre-racing.jpg' },
            { id: 'horror', name: 'Хоррор', img: '/icons/genre-horror.jpg' },
            { id: 'sport', name: 'Спорт', img: '/icons/genre-sport.jpg' },
            { id: 'rpg', name: 'RPG', img: '/icons/genre-rpg.jpg' },
            { id: 'shooter', name: 'Шутеры', img: '/icons/genre-shooter.jpg' },
            { id: 'puzzle', name: 'Головоломки', img: '/icons/genre-puzzle.jpg' },
          ].map(genre => (
            <DonateCard key={genre.id} onClick={() => navigate(COLLECTION_ROUTES[genre.id] || `/catalog?genre=${genre.name}`)}>
              <DonateImg src={genre.img} alt={genre.name} />
              <DonateOverlay />
              <DonateInfo>
                <DonateName>{genre.name}</DonateName>
              </DonateInfo>
            </DonateCard>
          ))}
        </CollScroll>
      </Section>

    </Page>
  );
};

export default Home;
